import React from 'react';
import { Link } from 'react-router-dom';

/**
 * ConsultantDashboardStats
 *
 * Props:
 * - stats: {
 *     active_sessions,
 *     completed_today,
 *     assigned_users,
 *     completed_engagements,
 *     total_this_week
 *   }
 */
const ConsultantDashboardStats = ({ stats }) => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 sm:gap-6 mb-6 sm:mb-8">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow">
        <div className="flex items-center">
          <div className="p-2 sm:p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex-shrink-0">
            <svg
              className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600 dark:text-emerald-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
          </div>
          <div className="ml-3 sm:ml-4 min-w-0 flex-1">
            <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white truncate">
              {stats.active_sessions || 0}
            </p>
            <p className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm truncate">
              Active Sessions
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow">
        <div className="flex items-center">
          <div className="p-2 sm:p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex-shrink-0">
            <svg
              className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 dark:text-blue-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div className="ml-3 sm:ml-4 min-w-0 flex-1">
            <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white truncate">
              {stats.completed_today || 0}
            </p>
            <p className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm truncate">
              Completed Today
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow">
        <div className="flex items-center">
          <div className="p-2 sm:p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex-shrink-0">
            <svg
              className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600 dark:text-purple-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
          </div>
          <div className="ml-3 sm:ml-4 min-w-0 flex-1">
            <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white truncate">
              {stats.assigned_users || 0}
            </p>
            <p className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm truncate">
              Assigned Users
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow">
        <div className="flex items-center">
          <div className="p-2 sm:p-3 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex-shrink-0">
            <svg
              className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600 dark:text-orange-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
              />
            </svg>
          </div>
          <div className="ml-3 sm:ml-4 min-w-0 flex-1">
            <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white truncate">
              {stats.completed_engagements || 0}
            </p>
            <p className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm truncate">
              Completed Engagements
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow">
        <div className="flex items-center">
          <div className="p-2 sm:p-3 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex-shrink-0">
            <svg
              className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600 dark:text-indigo-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
          <div className="ml-3 sm:ml-4 min-w-0 flex-1">
            <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white truncate">
              {stats.total_this_week || 0}
            </p>
            <p className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm truncate">
              This Week
            </p>
          </div>
        </div>
      </div>

      {/* White Label Branding Card - 6th Card */}
      <Link
        to="/consultant/branding"
        className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md hover:border-purple-300 dark:hover:border-purple-600 transition-all group"
      >
        <div className="flex items-center h-full">
          <div className="p-2 sm:p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex-shrink-0 group-hover:bg-purple-200 dark:group-hover:bg-purple-900/50 transition-colors">
            <svg
              className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600 dark:text-purple-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
              />
            </svg>
          </div>
          <div className="ml-3 sm:ml-4 min-w-0 flex-1">
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
              White Label
            </p>
            <p className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white truncate mt-1">
              Branding
            </p>
          </div>
          <div className="flex-shrink-0 ml-2">
            <svg
              className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        </div>
      </Link>
    </div>
  );
};

export default ConsultantDashboardStats;


